import React from 'react';
import {RawGenericDatePicker} from '../RawGenericDatePicker';
import {GenericDatePicker} from '../GenericDatePicker';
import {Icon} from '../../../../elements/Icon/Icon';
import {ErrorMessage, useField} from 'formik';
import 'react-datepicker/dist/react-datepicker.css';
import _ from 'lodash';
import {FormContainer} from '../../../form-containers/FormGroup';
import {FormErrorMessage} from '../../../form-labels/FormErrorMessage';
/**
 * @param {object} param0
 * @param {string} [param0.name]
 * Ensure to include the `name` property when using a Formik-type form control. Without it, the form control may not function properly.
 * Additionally, remember to wrap `<Formik></Formik>` around this and all other form control types to ensure proper functionality.
 * Adding the name to a Formik DayTimePicker component is essential because Formik uses the name prop to link the value of the  DayTimePicker to the Formik state.
 * Note that the Formik type is the default version injected into the form control you are applying.
 *
 * @param {boolean} [param0.raw]
 * Meant for non formik Input (Raw)
 * If set to true, a non-Formik version of the form control will be injected instead.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {string} [param0.placeholder]
 * The text displayed in the DayPicker when it is empty and not focused, providing a hint to the user about what information is expected.
 * Placeholder text follows a naming convention for consistency:
 *   - 'Select an option'
 *   - 'Pick an date'
 *
 * Avoid text like this:
 * --- date ----
 *
 *  @param {function} [param0.onChange]
 *  Set to a callback function with one argument, which stands for the date selected by the user. The function body should call the updater function returned by the useState hook to update the state.
 *
 * @param {function} [param0.selected]
 * set to the selected date, stored in the state. It is similar to value prop on **<input>** elements.
 *
 * @param {string} [param0.locale="en-US"]
 * Meant for non formik datepicker (Raw)
 *  Sets the locale used by the datepicker for displaying dates and formatting. The value should be a string representing a valid locale identifier, such as "en-US" for English (United States). Providing the appropriate locale ensures that dates are displayed in the desired language and format for your users.
 *
 * @param {string} [param0.timeFormat="HH:mm"]
 * Meant for non formik datepicker (Raw)
 * Sets the format for displaying and parsing time values in the datepicker's time selector. Use this prop to customize the time format according to your application's requirements.
 */

export function DayTimePicker({
    disabled,
    id,
    label,
    locale,
    name,
    onChange,
    raw,
    selected,
    timeFormat,
    validationError,
}) {
    const [, fieldMeta, fieldHelpers] = useField(name);

    return (
        <FormContainer
            disabled={disabled}
            errorMessage={
                raw ? (
                    <>{validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}</>
                ) : (
                    <ErrorMessage name={name} component={FormErrorMessage} />
                )
            }
            label={label}
            suffix={
                <>
                    <Icon name="calendarMonth" />
                </>
            }>
            {raw ? (
                <RawGenericDatePicker
                    dateFormat={'dd MMM yyyy, HH:mm'}
                    disabled={disabled}
                    id={id}
                    locale={locale}
                    name={name}
                    onChange={onChange}
                    selected={selected}
                    showTimeSelect={true}
                    specialClass="form-datepicker--day-time-picker"
                    timeFormat={timeFormat}
                />
            ) : (
                <GenericDatePicker
                    dateFormat={'dd MMM yyyy, HH:mm'}
                    disabled={disabled}
                    label={label}
                    name={name}
                    onChange={(v) => {
                        fieldHelpers.setValue(Date.parse(v));
                        if (_.isFunction(onChange)) onChange(v);
                    }}
                    onKeyDown={(e) => {
                        e.preventDefault();
                    }}
                    selected={fieldMeta.value ? new Date(+fieldMeta.value) : null}
                    showTimeSelect={true}
                    showWeekNumbers={false}
                    specialClass="form-datepicker--day-time-picker"
                />
            )}
        </FormContainer>
    );
}
